import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { ApiKeysService, NotificationService } from '../../../core/services';
import { ApiKey } from '../../../core/models';
import { MODAL_LAYOUT, injectModalRef } from '../../../shared/modal';

/**
 * Create an API key without leaving the list (was its own /api-keys/create
 * page; that URL now redirects to /api-keys?create=1, which opens this).
 * Two steps in one dialog: name the key, then show the new key with reveal
 * and copy. Done closes with true; the list reloads on any close.
 */
@Component({
  selector: 'app-create-key-modal',
  standalone: true,
  imports: [ReactiveFormsModule, ...MODAL_LAYOUT],
  templateUrl: './create-key-modal.html',
  styleUrl: './create-key-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateKeyModal {
  readonly modalRef = injectModalRef<boolean>();

  private readonly apiKeysService = inject(ApiKeysService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  isLoading = false;
  created = false;
  copied = false;
  revealed = false;
  apiKey = '';
  createdKeyName = '';

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    // No ✕/Escape/backdrop mid-request: the key would be created unseen.
    this.modalRef.setDisableClose(true);
    this.apiKeysService.createApiKey({ name: this.form.getRawValue().name })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.modalRef.setDisableClose(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (key: ApiKey) => {
          // Plaintext is only ever returned here it can't be fetched again after this.
          this.apiKey = key.key ?? '';
          this.createdKeyName = key.name || 'Untitled key';
          this.created = true;
          this.notificationService.success('API key generated successfully');
        },
        error: () => {
          this.notificationService.error('An error occurred while generating the key. Please try again later.');
        },
      });
  }

  toggleRevealed(): void {
    this.revealed = !this.revealed;
  }

  copyKey(): void {
    const showCopied = () => {
      this.copied = true;
      this.cdr.markForCheck();
      setTimeout(() => { this.copied = false; this.cdr.markForCheck(); }, 2000);
    };

    if (navigator.clipboard) {
      navigator.clipboard.writeText(this.apiKey).then(showCopied).catch(() => {
        this.fallbackCopy();
        showCopied();
      });
    } else {
      this.fallbackCopy();
      showCopied();
    }
  }

  private fallbackCopy(): void {
    const ta = document.createElement('textarea');
    ta.value = this.apiKey;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
}
